/**
 * One choice of a few, as a row of pills: a role, a filter, a view. A radio group, so arrows move the choice.
 */
import { useRef, type KeyboardEvent } from 'react'
import { cx } from '../cx'
import s from './Segmented.module.css'

export interface SegmentOption<V extends string = string> {
  value: V
  label: string
  count?: number
}

export function Segmented<V extends string>({ label, options, value, onChange, size = 'md' }: { label: string; options: SegmentOption<V>[]; value: V; onChange: (v: V) => void; size?: 'md' | 'sm' }) {
  const ref = useRef<HTMLDivElement>(null)
  const move = (e: KeyboardEvent, i: number) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = options[(i + step + options.length) % options.length]!
    onChange(next.value)
    ref.current?.querySelectorAll<HTMLButtonElement>('button')[options.indexOf(next)]?.focus()
  }
  return (
    <div ref={ref} role="radiogroup" aria-label={label} className={cx(s.seg, size === 'sm' && s.sm)}>
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => move(e, i)}
        >
          {o.label}
          {o.count !== undefined && <span className={s.n}>{o.count}</span>}
        </button>
      ))}
    </div>
  )
}
